import { useParams, useNavigate, useFetcher } from "react-router-dom";
import { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { useApplicationModals } from "app/common/hooks";
import { useCollector } from "app/integration/common/hooks";
import { IconButton } from "app/components/atoms/button";
import {
  EyeIcon,
  PencilSquareIcon,
  PrinterIcon,
  ServerStackIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Table from "app/components/atoms/table";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import { Currency } from "app/components/atoms/typography";
import { isUniqueBarcode } from "app/common/product";

function ProductDetail() {
  let { id } = useParams();

  const breadcrumbItems = [
    { label: "Product", href: "/product" },
    { label: "Product Variant", href: "#" },
  ];
  const { openModal, setPayloads } = useApplicationModals();

  const [productVariants, setProductVariant, productVariantLoader, collect] =
    useCollector({
      modelName: "productVariant",
      params: { product_id: id },
    });

  // Mode pelacakan ada di level PRODUK (`m_product.is_unique_barcode`), jadi seluruh
  // varian di halaman ini nilainya sama. Karena itu ditampilkan SEKALI sebagai badge,
  // bukan sebagai kolom yang berulang identik di tiap baris — kolom seperti itu akan
  // menyiratkan nilainya berbeda per varian, padahal tidak.
  //
  // Penafsiran nilainya diserahkan ke `isUniqueBarcode` di app/common/product —
  // termasuk alasan kenapa nilai yang tidak ada dibaca "Per Pcs".
  const isPerPcs = isUniqueBarcode(
    productVariants[0]?.product_detail?.is_unique_barcode
  );

  return (
    <MainLayout
      activeSidebarNavigation="product"
      activeSidebarChild="productVariant"
      breadcrumbItems={breadcrumbItems}
      pageTitle={
        productVariants[0]?.product_detail
          ? `${productVariants[0]?.product_detail?.name} (${productVariants[0]?.product_detail?.code})`
          : "Loading..."
      }
      headingButtons={[
        {
          label: "Find Code",
          type: "menuButton",
          onClick: () => openModal("productCode.find"),
          items: [
            {
              label: "Add Variant",
              onClick: () => {
                setPayloads("productVariant.create", {
                  id: id,
                });
                openModal("productVariant.create");
              },
            },
          ],
        },
      ]}
    >
      <AppModals
        items={[
          "productVariant.create",
          "productVariant.update",
          "qrCode.view",
          "productCode.find",
        ]}
        onSuccess={{
          "productVariant.create": () => collect({ product_id: id }),
          "productVariant.update": () => collect({ product_id: id }),
          "qrCode.view": () => collect(),
          "productCode.find": () => collect(),
        }}
      />
      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <Table.SearchBarItemWrapper>
              {/* <SearchInput testId="product-detail-search-input" placeholder="Search" /> */}
            </Table.SearchBarItemWrapper>
          </Table.SearchBarWrapper>
          {/* <Table.UtilitiesButtonWrapper>
            <IconButton testId="product-detail-printer-button" icon={PrinterIcon} />
          </Table.UtilitiesButtonWrapper> */}
        </Table.UtilitiesWrapper>
        {productVariants?.length > 0 && (
          <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">Pelacakan Stok:</span>
            {isPerPcs ? (
              <>
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                  Per Pcs — barcode unik tiap unit
                </span>
                <span className="text-xs text-gray-500">
                  Setiap unit yang diterima punya barcode sendiri; di POS qty terkunci 1
                  per scan.
                </span>
              </>
            ) : (
              <>
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                  Per Qty — tanpa barcode unik
                </span>
                <span className="text-xs text-gray-500">
                  Penerimaan barang tidak membuat barcode per unit; di POS dijual per qty
                  memakai QR varian.
                </span>
              </>
            )}
          </div>
        )}
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              {/* <Table.Heading>Kode</Table.Heading>
              <Table.Heading>Nama</Table.Heading> */}
              <Table.Heading>Size</Table.Heading>
              <Table.Heading>Warna</Table.Heading>
              <Table.Heading>Stok</Table.Heading>
              <Table.Heading>Sales Order</Table.Heading>
              <Table.Heading>Proses Pabrik</Table.Heading>
              <Table.Heading>Harga</Table.Heading>
              <Table.Heading></Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {productVariants?.map((productVariant, key) => (
              <Table.BodyRow key={key}>
                {/* <Table.Cell>{productVariant?.product_detail?.code}</Table.Cell>
                <Table.Cell>{productVariant?.product_detail?.name}</Table.Cell> */}
                <Table.Cell>{productVariant?.size?.name}</Table.Cell>
                <Table.Cell>{productVariant?.color?.name}</Table.Cell>
                <Table.Cell>{productVariant?.qty}</Table.Cell>
                <Table.Cell>{productVariant?.outgoing_qty}</Table.Cell>
                <Table.Cell>{productVariant?.incoming_qty}</Table.Cell>
                <Table.Cell>
                  <Currency number={productVariant?.product_detail?.price} />
                </Table.Cell>
                <Table.Cell>
                  <div className="flex">
                    <IconButton testId="product-detail-pencil-square-button"
                      icon={PencilSquareIcon}
                      onClick={() => {
                        setPayloads("productVariant.update", {
                          id: productVariant.id,
                        });
                        openModal("productVariant.update");
                      }}
                    />
                    <IconButton testId="product-detail-printer-button"
                      icon={PrinterIcon}
                      onClick={() => {
                        setPayloads("qrCode.view", {
                          product: productVariant,
                          multiple: false,
                        });
                        openModal("qrCode.view");
                      }}
                    />
                    <IconButton testId="product-detail-server-stack-button"
                      icon={ServerStackIcon}
                      href={`/stock-movement/${productVariant.id}`}
                    />
                    <IconButton testId="product-detail-eye-button"
                      icon={EyeIcon}
                      href={`/product-code/${productVariant.id}`}
                    />
                  </div>
                </Table.Cell>
              </Table.BodyRow>
            ))}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
    </MainLayout>
  );
}

export default ProductDetail;
